import {
  BaseFieldTypeLogic,
  type FilterEvaluationOptions,
} from "@smartsuite/fields-logic/lib/field-type-registry/field-logic";
import { getFieldLogic } from "@smartsuite/fields-logic/lib/field-type-registry/fields-logic-registry";
import {
  type Comparison,
  type DateModes,
  type FilterField,
  NewVocabularyDateModes,
  type Preferences,
} from "@smartsuite/types";
import memoize from "lodash.memoize";
import { fieldTypes } from "../registry/fields-logic/field-types-map";
import { Fields } from "../types/fields";
import type { FormConfigApplicationStructureItem } from "../types/form";
import { isBlank, prepareFormValueToComparer } from "./formValueFormatters";

// Comparisons where ALL values must satisfy the condition (as opposed to at least one).
const NEGATIVE_LOOKUP_COMPARISONS = new Set<Comparison>([
  "is_not",
  "not_contains",
  "is_not_equal_to",
  "is_none_of",
  "has_none_of",
]);

// Returns sorted list of filter rules, removes inapplicable rules.
// Rules is inapplicable if comparison value is required but not specified.
export function cleanFilterRules(rules: FilterField[]): FilterField[] {
  const emptyComparisons: Comparison[] = [
    "is_empty",
    "is_not_empty",
    "is_overdue",
    "is_not_overdue",
  ];
  const emptyDateModes: DateModes[] = [
    "next_number_of_days",
    "past_number_of_days",
    ...NewVocabularyDateModes.filter((dateMode) => dateMode.includes("_number_of_")),
    "date_range",
    "exact_date",
    "exact_date_time",
  ];

  // we take filter rules that either have a value or don't require a value
  return rules.filter(({ value, comparison }) => {
    // date field case
    if (value?.date_mode) {
      return !(emptyDateModes.includes(value.date_mode) && !value.date_mode_value);
    }
    return !isBlank(value) || emptyComparisons.includes(comparison);
  });
}

/**
 * Evaluates a lookup field condition against the current form value.
 * Returns the comparison result, or the fallback value when the condition is malformed.
 */
const evaluateLookupCondition = (
  filterField: FilterField,
  formValue: unknown,
  appField: FormConfigApplicationStructureItem,
  satisfyValueWhenInvalid: boolean,
  preferences: Preferences,
  options?: FilterEvaluationOptions,
): boolean => {
  const flatValues = Array.isArray(formValue)
    ? (formValue as unknown[]).flat(20).filter((v) => !isBlank(v))
    : [];

  if (filterField.comparison === "is_empty") return flatValues.length === 0;
  if (filterField.comparison === "is_not_empty") return flatValues.length > 0;

  const targetField = appField.params?.target_field_structure as
    | FormConfigApplicationStructureItem
    | undefined;
  const targetFieldType = targetField?.field_type as keyof typeof fieldTypes | undefined;
  const isTargetSupported =
    targetField && targetFieldType && fieldTypes[targetFieldType]?.name !== BaseFieldTypeLogic.name;

  if (isTargetSupported) {
    const comparer = getLookupComparer(targetField, filterField, preferences, options);
    if (comparer) {
      const normalised = prepareFormValueToComparer(formValue, appField, filterField.comparison);
      const normalisedFlat = Array.isArray(normalised) ? (normalised as unknown[][]).flat() : [];
      const isNegative = NEGATIVE_LOOKUP_COMPARISONS.has(filterField.comparison as Comparison);
      return isNegative
        ? normalisedFlat.every((v) => comparer(v, targetField))
        : normalisedFlat.some((v) => comparer(v, targetField));
    }
  }

  console.warn(
    `Cannot get a comparer generator for lookup field "${appField.label}" ` +
      `with target type "${targetField?.field_type}" and comparison "${filterField.comparison}". Ignoring it...`,
  );
  return satisfyValueWhenInvalid;
};

/**
 * Attempts to build a comparer function for a lookup target field and filter.
 * Returns the comparer function, or null if the field logic or comparison is unavailable.
 */
const getLookupComparer = (
  targetField: FormConfigApplicationStructureItem,
  filterField: FilterField,
  preferences: Preferences,
  options?: FilterEvaluationOptions,
): ((value: unknown, field: FormConfigApplicationStructureItem) => boolean) | null => {
  const targetFieldLogic = getFieldLogic(targetField.field_type);
  if (!targetFieldLogic) return null;

  const targetGenerator = targetFieldLogic.getComparisons(targetField, filterField, options)[
    filterField.comparison
  ];
  if (typeof targetGenerator !== "function") return null;

  const targetFilterValue = targetFieldLogic.processFilterValue(
    filterField.value,
    filterField.comparison,
    targetField,
    preferences,
    options,
  );
  return targetGenerator(targetFilterValue);
};

/**
 * Determines if the condition is satisfied.
 * - If the condition relies on an invalid App field, it is considered as malformed.
 * - If the condition relies on an App field that was not migrated to fields-logic repo yet,
 * it is considered as malformed.
 * - If we cannot grab the field logic of the App field, it is considered as malformed.
 * - If we cannot get a comparer method for the field type, it is considered as malformed.
 *
 * Whenever the condition is malformed, it returns the value in satisfyValueWhenInvalid
 * (default: true). Otherwise, it returns the result of the comparison.
 *
 * @param filterField The filter row configured on the Form Builder for the current field
 * @param formValues The value currently entered by the user in the form for the field, stored in Formik
 * @param appField The App field that the condition is configured for, from the App structure
 * @param satisfyValueWhenInvalid The default value to return when the condition is malformed
 * @returns True if the app field exists, the field logic exists, the comparison method exists,
 * and the comparison method returns true. Otherwise, returns the value of satisfyValueWhenInvalid.
 */
export const isConditionSatisfied = (
  filterField: FilterField,
  formValue: unknown,
  appField: FormConfigApplicationStructureItem | undefined,
  satisfyValueWhenInvalid: boolean,
  preferences: Preferences,
  options?: FilterEvaluationOptions,
): boolean => {
  if (!appField) {
    console.warn(`Rule using an invalid app field cannot be validated. Ignoring it...`);
    return satisfyValueWhenInvalid;
  }

  if (fieldTypes[appField.field_type].name === BaseFieldTypeLogic.name) {
    console.warn(
      `Rule using field of type "${appField.field_type}" cannot be validated yet. Ignoring the it...\nConsider removing the field "${appField.label}" from the conditions for now.`,
    );
    return satisfyValueWhenInvalid;
  }

  if (appField.field_type === Fields.lookupfield) {
    return evaluateLookupCondition(
      filterField,
      formValue,
      appField,
      satisfyValueWhenInvalid,
      preferences,
      options,
    );
  }

  const fieldLogic = getFieldLogic(appField.field_type);
  if (!fieldLogic) {
    console.warn(
      `Cannot get field logic of field with type "${appField.field_type}". Ignoring it...`,
    );
    return satisfyValueWhenInvalid;
  }

  const generator = fieldLogic.getComparisons(appField, filterField, options)[
    filterField.comparison
  ];
  if (typeof generator === "function") {
    const filterValue = fieldLogic.processFilterValue(
      filterField.value,
      filterField.comparison,
      appField,
      preferences,
      options,
    );
    const comparer = generator(filterValue);

    const preparedValue = prepareFormValueToComparer(formValue, appField, filterField.comparison);
    // A date-range condition targets a boundary (field "<slug>.from_date"/".to_date").
    // DateRangeFieldLogic resolves that boundary's date comparer, so feed it the boundary's
    // date value rather than the whole { from_date, to_date } object.
    const [, boundary] = filterField.field.split(".");
    const value =
      appField.field_type === Fields.daterangefield && boundary
        ? ((preparedValue as Partial<Record<"from_date" | "to_date", { date: unknown }>>)?.[
            boundary as "from_date" | "to_date"
          ]?.date ?? null)
        : preparedValue;

    return comparer(value, appField);
  } else {
    console.warn(
      `Cannot get a comparer generator for field with type "${appField.field_type}" and comparison "${filterField.comparison}". Ignoring it...\nCheck that the field "${appField.label}" has a valid comparison configured.`,
    );
    return satisfyValueWhenInvalid;
  }
};

export const CONDITION_CACHE_MAX_ENTRIES = 500;

// lodash.memoize-compatible Map wrapper with LRU eviction, to keep the
// per-session memory of condition evaluation bounded (the key embeds the
// stringified form value, so every keystroke creates a new entry).
export class LruCache<K, V> {
  private readonly map = new Map<K, V>();

  has(key: K): boolean {
    return this.map.has(key);
  }

  get(key: K): V | undefined {
    const value = this.map.get(key);
    if (this.map.has(key)) {
      // refresh recency
      this.map.delete(key);
      this.map.set(key, value as V);
    }
    return value;
  }

  set(key: K, value: V): this {
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);
    if (this.map.size > CONDITION_CACHE_MAX_ENTRIES) {
      this.map.delete(this.map.keys().next().value as K);
    }
    return this;
  }

  delete(key: K): boolean {
    return this.map.delete(key);
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }
}

// Memoize the isConditionSatisfied function to avoid unnecessary re-evaluations for when
// other form fields get altered. Using the information about the filter, form value, app field
// and fallback as the memoization key.
// The form value gets stringified because it may hold an array of values or an object.
export const isConditionSatisfiedMemoized = memoize(
  isConditionSatisfied,
  (filterField, formValue, appField, satisfyValueWhenInvalid, _preferences, options) => {
    // Add timestamp for date fields to force re-evaluation
    const timestamp = filterField.value?.date_mode ? new Date().toISOString().split("T")[0] : "";

    return `${filterField.field}-${filterField.comparison}-${JSON.stringify(
      filterField.value,
    )}-${JSON.stringify(formValue)}-${
      appField?.field_type
    }-${satisfyValueWhenInvalid}-${timestamp}-${!!options?.newDateVocabulary}`;
  },
);

// Bound the cache for this memoized function only — do not mutate the global
// memoize.Cache, which would affect every other lodash.memoize user.
isConditionSatisfiedMemoized.cache = new LruCache<string, boolean>();
